import { z } from 'zod';
import {
  invoiceReaderStatusSchema,
  invoiceReadingSchema,
  type InvoiceReadRequest,
  type InvoiceReading,
  type UserPatchInput,
  type UserUpsertInput,
} from '@agrogruda/shared/schemas';
import { sq } from '@/i18n/sq';
import { env } from './env';
import { supabase } from './supabase';

/** Error raised for every non-2xx response, network failure or malformed body of apps/api. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Attach the Supabase access token (default true). */
  auth?: boolean;
}

const errorBodySchema = z.object({ error: z.string(), message: z.string().optional() });

function parseJson(text: string): unknown {
  if (text.length === 0) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

async function accessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

export async function apiRequest<T>(
  path: string,
  schema: z.ZodType<T>,
  options: ApiRequestOptions = {},
): Promise<T> {
  const headers = new Headers({ Accept: 'application/json' });
  if (options.body !== undefined) headers.set('Content-Type', 'application/json');
  if (options.auth !== false) {
    const token = await accessToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  let response: Response;
  try {
    response = await fetch(`${env.apiUrl}${path}`, {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? null : JSON.stringify(options.body),
    });
  } catch (cause) {
    throw new ApiError(0, 'network', sq.errors.serviceUnavailable, { cause });
  }
  const json = parseJson(await response.text());
  if (!response.ok) {
    const parsed = errorBodySchema.safeParse(json);
    if (parsed.success) {
      throw new ApiError(response.status, parsed.data.error, parsed.data.message ?? sq.errors.requestFailed);
    }
    throw new ApiError(response.status, 'http_error', sq.errors.requestFailed);
  }
  const result = schema.safeParse(json);
  if (!result.success) {
    throw new ApiError(response.status, 'invalid_response', sq.errors.requestFailed, { cause: result.error });
  }
  return result.data;
}

export const authStatusSchema = z.object({ setupRequired: z.boolean() });
export type AuthStatus = z.infer<typeof authStatusSchema>;

export const idResponseSchema = z.object({ id: z.string() });
export type IdResponse = z.infer<typeof idResponseSchema>;

export interface SetupBody {
  name: string;
  username: string;
  password: string;
}

/**
 * Typed from the schemas the API validates these requests with. Hand-written, the worker link went out as
 * `employeeId` while the API reads `employee_id` and drops what it does not know, so no login was ever
 * linked to its worker.
 */
export type CreateUserBody = Omit<UserUpsertInput, 'id' | 'password'> & { password: string };
export type UpdateUserBody = UserPatchInput;

export const authApi = {
  status: (): Promise<AuthStatus> => apiRequest('/api/auth/status', authStatusSchema, { auth: false }),
  setup: (body: SetupBody): Promise<IdResponse> =>
    apiRequest('/api/auth/setup', idResponseSchema, { method: 'POST', body, auth: false }),
};

export const usersApi = {
  create: (body: CreateUserBody): Promise<IdResponse> =>
    apiRequest('/api/users', idResponseSchema, { method: 'POST', body }),
  update: (id: string, body: UpdateUserBody): Promise<IdResponse> =>
    apiRequest(`/api/users/${encodeURIComponent(id)}`, idResponseSchema, { method: 'PATCH', body }),
  remove: (id: string): Promise<IdResponse> =>
    apiRequest(`/api/users/${encodeURIComponent(id)}`, idResponseSchema, { method: 'DELETE' }),
};

/** NEW — the AI invoice reader (Blerje). */
export const invoicesApi = {
  aiAvailable: (): Promise<boolean> =>
    apiRequest('/api/invoices/reader', invoiceReaderStatusSchema).then((status) => status.ai),
  read: (body: InvoiceReadRequest): Promise<InvoiceReading> =>
    apiRequest('/api/invoices/read', invoiceReadingSchema, { method: 'POST', body }),
};

export function errorMessage(error: unknown, fallback: string = sq.errors.requestFailed): string {
  if (error instanceof Error && error.message.length > 0) return error.message;
  return fallback;
}
